import { useEffect, useRef } from 'react';
import { App } from '@capacitor/app';
import { useNavigate } from 'react-router-dom';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

import { secureSetItem } from '@utils';
import { onNativeLoginTokensUpdated } from '../apollo/client';

export default function useAppDeepLink() {
  const navigate = useNavigate();
  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let listener: PluginListenerHandle | null = null;

    const isValidAppUrl = (url: string) =>
      url.startsWith('https://festayre.com') ||
      url.startsWith('https://festayre.fr') ||
      url.startsWith('festayre://');

    const handleDeepLink = async ({ url }: { url: string }) => {
      if (!isValidAppUrl(url)) return;

      // Deduplicate iOS double events
      if (handledRef.current === url) return;
      handledRef.current = url;

      const parsedUrl = new URL(url);
      const pathWithQuery =
        parsedUrl.protocol === 'festayre:'
          ? `/${parsedUrl.host}${parsedUrl.pathname}${parsedUrl.search}`
          : parsedUrl.pathname + parsedUrl.search;

      const accessToken = parsedUrl.searchParams.get('accessToken');
      const refreshToken = parsedUrl.searchParams.get('refreshToken');

      if (accessToken && refreshToken) {
        console.log('[---> [set tokens] <---]:', true);
        await secureSetItem('accessToken', accessToken);
        await secureSetItem('refreshToken', refreshToken);

        // Let storage settle before resetting Apollo
        await new Promise((r) => setTimeout(r, 0));

        await onNativeLoginTokensUpdated();
      }

      if (import.meta.env.DEV) {
        console.log('[DeepLink]', {
          'Received URL': url,
          'Parsed Path': pathWithQuery,
        });
      }

      if (parsedUrl.protocol === 'festayre:') {
        await Browser.close();
      }

      navigate(pathWithQuery, { replace: true });
    };

    const setup = async () => {
      try {
        listener = await App.addListener('appUrlOpen', handleDeepLink);
        if (import.meta.env.DEV) console.log('[DeepLink] Listener registered');
      } catch (err) {
        console.error('[DeepLink] Failed to register listener:', err);
      }
    };

    setup();

    return () => {
      listener?.remove();
    };
  }, [navigate]);
}
